import React, {useEffect, useRef, useState} from "react";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {loadWardPatientsByWardId, updateWardPatientsByWardId, wardPatients} from "../../store/reducers/ward";
import makeStyles from "@mui/styles/makeStyles";
import InformationBox from "../../components/other/informationBox";
import {AgyleTable, createAgyleTableColumn} from "../../components/table/AgyleTable";
import moment from "moment";
import config from "../../app/config";
import {useTranslation} from "react-i18next";
import AgyleHeader from "../../components/header/agyleHeader/agyleHeader";
import {getZoneAndBedsNameFromOldMapUHD} from "../../util/oldMapUtil";
import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import {Paper} from '@mui/material';
import {setSnackbar} from '../../store/reducers/globals';
import {ESCALATION_LEVEL} from '../reports/extendedDashboard';
import {calculateArrivalDate} from '../../util/patientUtil';
import {getPatientFlagValue} from './mainScreen/util/patientFlagsUtil';
import {patientSafetyChecklistSectionValidation} from './forms/formsUtil';
import {oldBedsDict} from '../../store/reducers/dicts';
import {getConfig} from '../../util/configUtil';

const useStyles = makeStyles((theme) => ({
    root: {
      padding: 10
    },
    agyleMenu: {
        paddingTop: 2,
        margin: "0 5px",
    },
    paper: {
        padding: 10,
        borderRadius: 10,
        marginBottom: 5,
    },
    row: {
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "center",
    },
    title: {
        fontWeight: "bold",
        fontSize: "xx-large",
        marginLeft: "auto",
        marginRight: "auto",
        fontFamily: "Inter",
    },
    hospital: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 'large',
        fontStyle: 'italic',
        fontWeight: 'bold',
        '&.right': {
            marginLeft: 'auto',
            marginRight: 10,
        },
    },
    stats: {
        width: '100%',
        justifyContent: 'center',
    },
    statisticTitle: {
        fontSize: 13,
        paddingLeft: 5,
        width: '70%',
        textAlign: 'right',
        fontWeight: 'bold',
    },
    statisticBlock: {
        cursor: 'auto',
        color: '#000',
        width: '20%',
        minHeight: 20,
        backgroundColor: '#FFF',
        fontSize: 'small',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 5,
    },
    escalationLevel: {
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#262626',
        '&.normal': {
            backgroundColor: '#92d050',
        },
        '&.moderate': {
            backgroundColor: '#ffc000',
        },
        '&.severe': {
            backgroundColor: '#ff0000',
            color: '#fff',
        },
        '&.extreme': {
            backgroundColor: '#7030a0',
            color: '#fff',
        },
    },
}));

function createData(id, title, beds) {
    return {id, title, beds};
}

export const POOLE_STATISTICS = [
    createData("majorsA", "Majors A", ["M1", "M2", "M3", "M4", "M5", "M6", "M/h"]),
    createData("majorsB", "Majors B", ["M8", "M9", "M10", "M11", "M12", "M13", "C1", "C2", "C3", "C4"]),
    createData("aca", "ACA", ["ACA1", "ACA2", "ACA3", "ACA4", "ACA5", "ACA6", "wlist6", "wlist7"]),
    createData("pipAmb", "Pitstop/Ambulance", ["ps1", "ps2", "ps3", "ps4", "wlist4", "wlist9", 'wlist8']),
    createData("resus", "Resus", ["Resus1", "Resus2", "Resus3", "Resus4"]),
    createData("paed", "Paeds ED", ["Child1", "Child2", "Child3", "Child4"]),
    createData("triageUTC", "Triage UTC", ["wlist5", "wlist2", "T1", "Exam1", "Treat1", 'wlist3', "Consult1", "Consult2", "Consult3", "Consult4", "Consult5", "Consult6", "Consult7", "Consult8", 'wlist11']),
    createData("triage", "Triage", ["wlist1", "Triage"]),
    createData("relatives", "Relatives", ["Relatives"]),
    createData("corridor", "Corridor List", ["wlist8"]),
];

export const RBH_STATISTICS = [
    createData("majorsA", "Majors A", ["rbhm1", "rbhm2", "rbhm3", "rbhm4", "rbhm5", "rbhm6", "rbhm7", "rbhm9", "rbhm10", "rbhm11", "rbhm12"]),
    createData("majorsB", "Majors B", ["rbhm13", "rbhm14", "rbhm15", "rbhm16", "rbhm17", "rbhm18", "rbhm19"]),
    createData("aca", "ACA", ["rbhaca1", "rbhaca2", "rbhaca3", "rbhaca4", "rbhaca5", "rbhaca6", "wlist8rbh"]),
    createData("ratAmb", "RATS/Ambulance", ["rbhrats1", "rbhrats2", "rbhrats3", "rbhrats4", "rbhmh", "rbhbutterfly", "wlist7rbh", "wlist9rbh", 'wlist6rbh']),
    createData("resusPaed", "Resus/Paeds", ["rbhres1", "rbhres2", "rbhres3", "rbhchild1", "rbhchild2", "rbhchild3"]),
    createData("obs", "Observations Bay", ["rbhobs1", "rbhobs2", "rbhobs3", "rbhobs4", "rbhobs5", "rbhobs6", "rbhobs7", "rbhobs8"]),
    createData("triageUTC", "Triage UTC", ["wlist3rbh", "wlist4rbh", "wlist2rbh", "wlist12rbh", "rbhtutc1", "rbhtutc2", "rbhplaster", "rbhminors1", "rbhminors2", "rbhminors3", "rbhminors4", "rbhminors5", "rbhminors6", "rbhminors7", "rbhminors8", "rbhminors9", "rbhminors10", "rbhconsult1", "rbhconsult2", "rbhconsult3", "rbhconsult4", "rbhconsult5"]),
    createData("consult", "Consult", ["wlist2rbh", "rbhconsult1", "rbhconsult2", "rbhconsult3", "rbhconsult4", "rbhconsult5"]),
    createData("minors", "Minors", ["rbhminors1", "rbhminors2", "rbhminors3", "rbhminors4", "rbhminors5", "rbhminors6", "rbhminors7", "rbhminors8", "rbhminors9", "rbhminors10", "rbhminors11", "rbhminors12", 'wlist4rbh', 'wlist2rbh']),
    createData("triage", "Triage", ["wlist1", "wlist11rbh", "rbht1", "wlist10rbh"]),
    createData("corridor", "Corridor List", ["wlist6rbh"]),
];

export const RBH_NEW_STATISTICS = [
    createData('utc', 'UTC', ['nrbhautc1', 'nrbhautc2', 'nrbhautc3', 'nrbhautc4', 'nrbhautc5', 'nrbhautc6', 'nrbhautc7', 'nrbhautc8', 'nrbhautc9', 'nrbhautc10', 'nrbhautc11', 'nrbhautc12', 'nrbhautcroomPlaster', 'nrbhautcroomTreat', 'wlist9nrbh', 'wlist6nrbh', 'wlist8nrbh']),
    createData('childED', 'Children\'s ED', ['nrbhced1', 'nrbhced2', 'nrbhced4', 'nrbhced5(MH)', 'nrbhced6(MH)', 'nrbhced7', 'nrbhced8', 'nrbhced9', 'nrbhced10', 'nrbhced11', 'nrbhcedTreat', 'nrbhcedPlaster', 'wlist14nrbh', 'wlist12nrbh', 'wlist11nrbh']),
    createData('rats', 'RATS', ['nrbhrats1', 'nrbhrats2(Chairs)', 'nrbhrats3(Chairs)', 'nrbhrats4(Chairs)', 'nrbhrats5', 'nrbhrats6', 'nrbhrats7', 'nrbhrats8', 'nrbhrats9', 'nrbhrats10', 'nrbhrats11', 'nrbhiso1', 'wlist6nrbh', 'wlist5nrbh', 'wlist4nrbh', 'wlist3nrbh']),
    createData('aca', 'ACA', ['nrbhaca1', 'nrbhaca2', 'nrbhaca3', 'nrbhaca4', 'nrbhaca5', 'nrbhaca6', 'nrbhaca7', 'nrbhaca8', 'nrbhaca9', 'nrbhaca10', 'nrbhmajorsc/e1', 'nrbhmajorsc/e2', 'wlist7nrbh']),
    createData('majors', 'Majors A', ['nrbhmajors1', 'nrbhmajors2', 'nrbhmajors3', 'nrbhmajors4', 'nrbhmajors5', 'nrbhmajors6', 'nrbhmajors7', 'nrbhmajors8', 'nrbhmajors9(MH)', 'nrbhmajors10(MH)', 'nrbhmajinterview1', 'nrbhmajinterview2']),
    createData('majorsB1', 'Majors B - 1/2', ['nrbhmajors11', 'nrbhmajors12', 'nrbhmajors13', 'nrbhmajors14', 'nrbhmajors15', 'nrbhmajors16', 'nrbhmajors17(SR)', 'nrbhmajors18(SR)']),
    createData('majorsB2', 'Majors B - 2/2', ['nrbhmajors18(SR)', 'nrbhmajors19(SR)', 'nrbhmajors20(SR)', 'nrbhmajors21', 'nrbhmajors22', 'nrbhmajors23', 'nrbhmajors24', 'nrbhmajors25', 'nrbhmajors26']),
    createData('resus', 'Resus', ['nrbhresus1', 'nrbhresus2', 'nrbhresus3', 'nrbhresus4', 'nrbhresus5', 'nrbhresus6', 'nrbhresus7', 'nrbhresus8']),
];

const COLUMNS = [
    createAgyleTableColumn("bed", "Location", 1, "bed", "string", "notSort", "medium", "left", true, false),
    createAgyleTableColumn("name", "Patient Name", 2, "name", "string", "", "medium", "left", true, true),
    createAgyleTableColumn("time", "time in dept", 2, "time", "time", "", "medium", "left", true, true),
    createAgyleTableColumn("obs", "OBS Due", 3, "obsTime", "obsTimeExtended", "", "medium", "left", true, false),
    createAgyleTableColumn("obsScore", "OBS Score", 4, "obs", "obs", "", "medium", "left", true, false),
    createAgyleTableColumn("psc", "Admission and Safety Checklist", 5, "psc", "pscTime", "", "medium", "left", true, true),
    createAgyleTableColumn("nursePlan", "Nurse Plan", 7, "nursePlan", "plans", "notSort", "medium", "left", true, false),
    createAgyleTableColumn("treatmentsPlan", "Treatments Plan", 8, "treatmentsPlan", "plans", "notSort", "medium", "left", false, false),
    createAgyleTableColumn("bedStatus", "Bed Status", 9, "bedStatus", "string", "notSort", "medium", "left", false, false),
    createAgyleTableColumn("referred", "Referred", 10, "referred", "referrals", "notSort", "medium", "left", false, false),
    createAgyleTableColumn("clinician", "Clinician Name", 11, "clinician", "string", "notSort", "medium", "left", false, false),
];

const StatisticED = ({zone, hospital, open, onClose}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const {t} = useTranslation();

    const wardPatientsObject = useSelector(wardPatients);

    const [filteredPts, setFilteredPts] = useState([]);

    const bedDict = useSelector(oldBedsDict);

    // Map state
    const [mapState, setMapState] = useState(null);
    const refContainer = useRef(null);
    const refContainerElement = useRef(null);
    const [formattedZones, setFormattedZones] = useState([]);

    // Top Statistic
    const [data, setData] = useState(null);
    const [escalationLevel, setEscalationLevel] = useState(null);

    useEffect(() => {
        const ed = `ED##${hospital?.hospitalId}`;
        dispatch(loadWardPatientsByWardId([ed], false, true));

        const hospitals = getConfig('modules.map.customMapConfig', []);
        const newRbhId = hospitals.find(h => h.shortName === 'RBHB')?.hospitalId;

        getZoneAndBedsNameFromOldMapUHD(refContainer, refContainerElement, hospital?.hospitalId === "R0D01", hospital?.hospitalId === "R0D02" || (newRbhId ? hospital?.hospitalId === newRbhId : false))
            .then(r => setMapState(r))
    }, [hospital, open]);

    // Format zones
    useEffect(() => {
        const format = [];
        _.forEach(mapState, zone => {
            const objZone = {
                areaTitle: zone.areaTitle,
                dataName: zone.dataName,
                beds: [],
                hospital: zone.hospital,
            };

            _.forEach(zone.beds, bed => {
                objZone.beds.push(bed.parsed.params[1]);
            });

            format.push(objZone);
        });
        setFormattedZones(format);
    }, [mapState]);

    // Check if patients data changes
    useEffect(() => {
        const UPDATE_FREQUENCY = 3000; // 3sek
        let interval;
        const ed = `ED##${hospital?.hospitalId}`;
        const updateData = () => {
            dispatch(updateWardPatientsByWardId([ed], true));
        };

        interval = setInterval(updateData, UPDATE_FREQUENCY);
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [hospital, open, formattedZones]);

    useEffect(() => {
        let interval;
        getData();
        interval = setInterval(getData, 5000);

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [hospital, formattedZones, wardPatientsObject]);

    useEffect(() => {
        if (data) {
            setEscalationLevel(calculateEscalationLevel());
        }
    }, [data]);

    useEffect(() => {
        if (!_.isNil(zone)) {
            const pts = [];
            _.forEach(wardPatientsObject, pt => {
                if (zone.beds.includes(pt.bed) || (_.isNil(pt.bed) && zone.beds.includes("wlist1"))) {
                    const name = pt?.patient?.familyName;
                    const obs = pt?.patient?.lastObservation?.nextObsDate || null;
                    const obsStatus = pt?.patient?.lastObservation?.nextObsStatus;
                    const obsTime = (obsStatus === "cancelled" || obsStatus === "suspended") ? obsStatus : obs;
                    const time = moment(calculateArrivalDate(pt));
                    let psc = null;
                    const nursePlan = _.filter(pt?.studies?.nursingPlanNote?.notes, note => !note.checked);
                    const treatmentsPlan = _.filter(pt?.studies?.treatmentPlan?.notes, note => !note.checked);
                    let bed = 'wlist1';
                    let clinician = "-";
                    let bedStatus = "-";
                    const referred = pt?.studies?.referrals?.ReferralsToOtherServices || []

                    if (pt.bed) {
                        if (_.startsWith(pt.bed, "rbh")) bed = pt.bed.substring(3).toUpperCase();
                        else if (_.startsWith(pt.bed, "nrbh")) bed = pt.bed.substring(4).toUpperCase();
                        else if (!_.startsWith(pt.bed, "wlist")) bed = pt.bed.toUpperCase();
                        else bed = pt.bed;
                    }

                    if (_.startsWith(bed, 'wlist')) {
                        const findWListBed = _.find(bedDict, {__id: bed});
                        bed = findWListBed?.location ? findWListBed?.location?.toUpperCase() : bed.toUpperCase();
                    }

                    if (pt?.studies?.safetyChecklist) {
                        const form = pt?.studies?.safetyChecklist;
                        const validSection1 = patientSafetyChecklistSectionValidation(form, 1, pt?.patient);
                        const validSection2 = validSection1 && patientSafetyChecklistSectionValidation(form, 2);
                        const validSection3 = validSection2 && patientSafetyChecklistSectionValidation(form, 3);
                        const validSection4 = validSection3 && patientSafetyChecklistSectionValidation(form, 4);

                        psc = `1st check incomplete / ${moment(form?.lastUpdateDate).format(config.defaultTimeFormat)}`;
                        if (validSection1 && form?.sectionDue1 && !form?.section1Lock) {
                            psc = moment(form?.sectionDue1?.timestamp).add(form?.sectionDue1?.value, 'hours');
                        } else if (!validSection2 && form?.section1Lock) {
                            psc = `2nd check incomplete / ${moment(form?.lastUpdateDate).format(config.defaultTimeFormat)}`;
                        } else if (validSection2 && form?.sectionDue2 && !form?.section2Lock) {
                            psc = moment(form?.sectionDue2?.timestamp).add(form?.sectionDue2?.value, 'hours');
                        } else if (!validSection3 && form?.section2Lock) {
                            psc = `3rd check incomplete / ${moment(form?.lastUpdateDate).format(config.defaultTimeFormat)}`;
                        } else if (validSection3 && form?.sectionDue3 && !form?.section3Lock) {
                            psc = moment(form?.sectionDue3?.timestamp).add(form?.sectionDue3?.value, 'hours');
                        } else if (!validSection4 && form?.section3Lock) {
                            psc = `4th check incomplete / ${moment(form?.lastUpdateDate).format(config.defaultTimeFormat)}`;
                        } else if (validSection4 && form?.sectionDue4 && !form?.section4Lock) {
                            psc = moment(form?.sectionDue4?.timestamp).add(form?.sectionDue4?.value, 'hours');
                        }

                        if (!_.isEmpty(form?.nextHours)) {
                            const last = _.last(form?.nextHours);
                            if (patientSafetyChecklistSectionValidation(last, last?.counter === 6 ? 6 : 0, pt) && last?.sectionDue) {
                                psc = moment(last?.sectionDue?.date).add(last?.sectionDue?.value, 'hours');
                            } else {
                                psc = `${last?.counter}th check incomplete / ${moment(form?.lastUpdateDate).format(config.defaultTimeFormat)}`;
                            }
                        }
                    }

                    if (pt?.currentClinician) {
                        clinician = pt?.currentClinician?.__description || '-';
                    }

                    if (pt?.studies?.bedRequest) {
                        const val = pt.studies.bedRequest;
                        if (val?.requested) bedStatus = "Requested";
                        if (val?.allocated) bedStatus = "Allocated";
                        if (val?.ready) bedStatus = "Ready";
                        if (val?.canceled) bedStatus = "Canceled";
                    }

                    pts.push({
                        id: pt.__id,
                        time,
                        bed,
                        name,
                        obsTime,
                        obs: pt?.patient?.lastObservation?.totalScore,
                        psc,
                        nursePlan,
                        treatmentsPlan,
                        referred,
                        bedStatus,
                        clinician,
                    });
                }
            });
            setFilteredPts(getFinalRows(_.orderBy(pts, ["arrival"], ["asc"])));
        } else {
            setFilteredPts([]);
        }
    }, [zone, wardPatientsObject, mapState]);

    const calculateEscalationLevel = () => {
        let innerLevel = {
            waitTriage: ESCALATION_LEVEL.normal,
            waitingSeen: ESCALATION_LEVEL.normal,
            waitingSeenTime: ESCALATION_LEVEL.normal,
            waitingSeenACA: ESCALATION_LEVEL.normal,
            waitingSeenACATime: ESCALATION_LEVEL.normal,
            waitingSeenUTC: ESCALATION_LEVEL.normal,
            waitingSeenUTCTime: ESCALATION_LEVEL.normal,
        };

        // Wait Triage
        const waitTriage = getMinutesFromLongest(data.waitTriageLongest?.date);
        if (waitTriage > 60) innerLevel.waitTriage = ESCALATION_LEVEL.extreme;
        else if (waitTriage > 40) innerLevel.waitTriage = ESCALATION_LEVEL.severe;
        else if (waitTriage > 15) innerLevel.waitTriage = ESCALATION_LEVEL.moderate;

        // Waiting to be Seen
        const waitSeen = getMinutesFromLongest(data.waitingSeenLongest?.date);
        if (waitSeen > 240) innerLevel.waitingSeenTime = ESCALATION_LEVEL.extreme;
        else if (waitSeen > 120) innerLevel.waitingSeenTime = ESCALATION_LEVEL.severe;
        else if (waitSeen > 60) innerLevel.waitingSeenTime = ESCALATION_LEVEL.moderate;
        const waitSeenPts = data.waitingSeen?.length;
        if (waitSeenPts > 15) innerLevel.waitingSeen = ESCALATION_LEVEL.extreme;
        else if (waitSeenPts > 10) innerLevel.waitingSeen = ESCALATION_LEVEL.severe;
        else if (waitSeenPts > 5) innerLevel.waitingSeen = ESCALATION_LEVEL.moderate;

        // Waiting to be Seen ACA
        const waitSeenACA = getMinutesFromLongest(data.waitingSeenACALongest?.date);
        if (waitSeenACA > 240) innerLevel.waitingSeenACATime = ESCALATION_LEVEL.extreme;
        else if (waitSeenACA > 120) innerLevel.waitingSeenACATime = ESCALATION_LEVEL.severe;
        else if (waitSeenACA > 60) innerLevel.waitingSeenACATime = ESCALATION_LEVEL.moderate;
        const waitSeenACAPts = data.waitingSeenACA?.length;
        if (waitSeenACAPts > 15) innerLevel.waitingSeenACA = ESCALATION_LEVEL.extreme;
        else if (waitSeenACAPts > 10) innerLevel.waitingSeenACA = ESCALATION_LEVEL.severe;
        else if (waitSeenACAPts > 5) innerLevel.waitingSeenACA = ESCALATION_LEVEL.moderate;

        // Waiting to be Seen UTC
        const waitSeenUTC = getMinutesFromLongest(data.waitingSeenUTCLongest?.date);
        if (waitSeenUTC > 240) innerLevel.waitingSeenUTCTime = ESCALATION_LEVEL.extreme;
        else if (waitSeenUTC > 120) innerLevel.waitingSeenUTCTime = ESCALATION_LEVEL.severe;
        else if (waitSeenUTC > 60) innerLevel.waitingSeenUTCTime = ESCALATION_LEVEL.moderate;
        const waitSeenUTCPts = data.waitingSeenUTC?.length;
        if (waitSeenUTCPts > 15) innerLevel.waitingSeenUTC = ESCALATION_LEVEL.extreme;
        else if (waitSeenUTCPts > 10) innerLevel.waitingSeenUTC = ESCALATION_LEVEL.severe;
        else if (waitSeenUTCPts > 5) innerLevel.waitingSeenUTC = ESCALATION_LEVEL.moderate;

        return innerLevel;
    };

    const getMinutesFromLongest = (time) => {
        if (time) {
            if (_.startsWith(time, '+')) return 99999999;
            const timeS = time.split(':');
            const hour = Number(timeS[0]);
            const minute = Number(timeS[1]);
            return (hour * 60) + minute;
        }

        return 0;
    };

    const getData = () => {
        const waitTriage = [];
        let waitTriageLongest = {id: null, date: null};

        const waitingSeen = [];
        let waitingSeenLongest = {id: null, date: null};

        const waitingSeenACA = [];
        let waitingSeenACALongest = {id: null, date: null};

        const waitingSeenUTC = [];
        let waitingSeenUTCLongest = {id: null, date: null};

        try {
            _.forEach(wardPatientsObject, element => {
                let elementHidden = false;
                if (_.startsWith(element?.bed, 'wlist') || !element?.bed) {
                    if (element.admission?.locationDescription === 'DISCHARGE') {
                        elementHidden = true;
                    }
                }

                if (!elementHidden && element?.admission?.status !== 'DISCHARGE_REQUESTED') {
                    const isEmOb = getPatientFlagValue(element, "emob");
                    if (!isEmOb) {
                        if (patientInZones(['WaitTriage', 'Wait Triage'], element, true)) {
                            if (!element?.triage?.discriminator && element?.studies?.seeAndTreat?.status !== 'completed') {
                                waitTriage.push(element);
                                checkLongestAwait(waitTriageLongest, element);
                            }
                        }

                        if (patientInZones(['Sub Wait', 'AWAITINGTriage UTC', 'POST TRIAGEUTC', 'Post TriageUTC', 'Child Wait UTC ', 'UTC WAITINGPre Triage', 'TRIAGE UTC', 'EXAM ROOM', 'TREAT ROOM', 'CONSULT A', 'CONSULT B', 'PLASTER', 'MINORS'], element)) {
                            if (!element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && _.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                                waitingSeenUTC.push(element);
                                checkLongestAwait(waitingSeenUTCLongest, element);
                            }
                        }

                        if (patientInZones(['CORRIDORList',  'AMBULANCEQueue', 'AMBULANCECohort List', 'AMBULANCEList', 'RESUS', 'MAJORS A', 'MAJORS B', 'MINORS A', 'MH', 'M/h', 'Obs A', 'Obs B', 'WaitTriage'
                            , 'Wait Triage', 'CHILDRENS', 'RATs', 'PITSTOP', 'TRIAGE', 'Child Wait', 'Sub Wait'], element, true)) {
                            if (!element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && !element?.seniorReview?.who && _.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                                waitingSeen.push(element);
                                checkLongestAwait(waitingSeenLongest, element);
                            }
                        }

                        if (patientInZones(['ACAWaiting', 'ChildrenWaiting', 'ACA', 'PostTriage'], element)) {
                            if (!element?.studies?.seeAndTreat?.clinician && !element?.currentClinician?.__description && !element?.seniorReview?.who && _.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                                waitingSeenACA.push(element);
                                checkLongestAwait(waitingSeenACALongest, element);
                            }
                        }
                    }
                }
            });
        } catch (e) {
            console.warn(e);
            dispatch(setSnackbar({type: "error", message: 'An error occurred while loading data.'}));
        }

        transformPrettyDateFormat(waitTriageLongest);
        transformPrettyDateFormat(waitingSeenLongest);
        transformPrettyDateFormat(waitingSeenACALongest);
        transformPrettyDateFormat(waitingSeenUTCLongest);
        setData({
            waitTriage,
            waitTriageLongest,
            waitingSeen,
            waitingSeenLongest,
            waitingSeenACA,
            waitingSeenACALongest,
            waitingSeenUTC,
            waitingSeenUTCLongest,
        });
    };

    const transformPrettyDateFormat = (obj, numberObj) => {
        if (obj.date && obj.id) {
            obj.date = transformDate(moment().diff(obj.date, 'minutes'));
        }
        if (numberObj) {
            return transformDate(obj);
        }
    };

    const transformDate = (diff) => {
        const hours = Math.floor(diff / 60);
        if (hours >= 100) {
            return '+99:00';
        }

        const hoursText = hours >= 10 ? hours : `0${hours}`;
        const min = Math.floor(diff - (hours * 60));
        const minsText = diff - (hours * 60) >= 10 ? min : `0${min}`;

        return `${hoursText}:${minsText}`;
    };

    const patientInZones = (zoneNames, pt, startListCheck, hospital) => {
        let ptBed = pt?.bed;
        let check = false;

        if (startListCheck) {
            ptBed = ptBed ? ptBed : 'wlist1';
        }

        const zonesF = _.filter(formattedZones, z => {
            if (hospital) return zoneNames.includes(z.areaTitle) && z.hospital === hospital;
            return zoneNames.includes(z.areaTitle);
        });

        _.forEach(zonesF, z => {
            if (check) return;
            check = z.beds.includes(ptBed);
        });

        return check;
    };

    const checkLongestAwait = (obj, pt) => {
        if (!obj?.id) {
            obj.id = pt.__id;
            obj.date = pt?.admission?.startDate;
        } else {
            if (moment(pt?.admission?.startDate).isBefore(moment(obj.date))) {
                obj.id = pt.__id;
                obj.date = pt?.admission?.startDate;
            }
        }
    };

    const getFinalRows = (pts) => {
        let tempPt = [...pts];
        const finalPts = [];
        const beds = _.filter(zone.beds, bed => !_.startsWith(bed, "wlist"));
        _.forEach(beds, bed => {
            let newBed = _.startsWith(bed, "rbh") ? bed.substring(3).toUpperCase() : bed;
            newBed = _.startsWith(bed, "nrbh") ? bed.substring(4).toUpperCase() : newBed.toUpperCase();
            const pt = _.find(tempPt, {bed: newBed});
            if (pt) {
                _.remove(tempPt, {bed: newBed});
                finalPts.push(pt);
            } else {
                const zone = fineZoneForBed(bed);
                finalPts.push({
                    id: Math.random(),
                    arrival: null,
                    bed: newBed,
                    zone,
                    name: "",
                    obsTime: null,
                    obs: -1,
                    psc: null,
                    nursePlan: [],
                    treatmentsPlan: [],
                    request: "",
                });
            }
        });

        if (!_.isEmpty(tempPt)) finalPts.unshift(...tempPt);
        return finalPts;
    };

    const fineZoneForBed = (bed) => {
        let zone = null;
        _.forEach(mapState, map => {
            if (zone) return;
            _.forEach(map?.beds, m => {
                if (zone) return;
                if (m?.parsed?.params?.[1] === bed) zone = map;
            })
        })
        return zone ? _.startCase(zone?.areaTitle) : "";
    }

    const renderStatistic = (text, bottomText, field, fieldLongest, escalationLvl, longestEscalationLevel) => {
        function getData(filedPath) {
            if (data && data[filedPath]) {
                const value = data[filedPath];
                if (_.isArray(value)) return value.length;
                if (_.isObject(value)) return value.date || '00:00';
                if (_.isString(value)) return value;
            }
            return 0;
        }

        function renderBlock(fieldPath, isTop, escalation) {
            const escalationFiled = _.get(escalationLevel, `${escalation}`);
            return (
                <div
                    className={clsx(
                        classes.statisticBlock, isTop ? 'top' : 'bottom',
                        escalation && classes.escalationLevel, escalationFiled,
                    )}
                >
                    <span style={{fontWeight: 'bold', textAlign: 'center'}}>{
                        getData(fieldPath)}
                    </span>
                </div>
            );
        }

        return (
            <Grid container direction="row" spacing={1} style={{maxWidth: '20%'}}>
                <Grid item xs={12} className={classes.row} style={{justifyContent: 'space-around'}}>
                    <div className={classes.statisticTitle}>
                        {text}
                    </div>
                    {renderBlock(field, true, escalationLvl)}
                </Grid>

                <Grid item xs={12} className={classes.row} style={{justifyContent: 'space-around'}}>
                    <div className={classes.statisticTitle}>
                        {bottomText}
                    </div>

                    {renderBlock(fieldLongest, false, longestEscalationLevel)}
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            <AgyleHeader onBack={onClose}>
                <div className={classes.hospital}>
                    {zone?.title || ""}
                </div>
                <div className={clsx(classes.hospital, 'right')}>
                    {hospital?.name || ""}
                </div>
            </AgyleHeader>

            <div className={classes.root}>
                <Paper className={classes.paper}>
                    <Grid item xs={12} className={clsx(classes.row, classes.stats)}>
                        {renderStatistic('Waiting to be seen in Majors', 'Longest wait to be seen', 'waitingSeen', 'waitingSeenLongest', 'waitingSeen', 'waitingSeenTime')}
                        {renderStatistic('Waiting to be seen ACA', 'Longest Wait to be seen', 'waitingSeenACA', 'waitingSeenACALongest', 'waitingSeenACA', 'waitingSeenACATime')}
                        {renderStatistic('Awaiting Triage', 'Longest Wait', 'waitTriage', 'waitTriageLongest', null, 'waitTriage')}
                        {renderStatistic('Waiting to be seen UTC', 'Longest Wait to be seen', 'waitingSeenUTC', 'waitingSeenUTCLongest', 'waitingSeenUTC', 'waitingSeenUTCTime')}
                    </Grid>
                </Paper>

                {!zone &&
                    <InformationBox type="info">
                        {t("Please choose a zone/bay from the list")}
                    </InformationBox>
                }

                {(_.isEmpty(filteredPts) && zone) &&
                    <InformationBox type="info">
                        {t("No patients have been found for this zone/bay")}
                    </InformationBox>
                }

                {!_.isEmpty(filteredPts) &&
                    <AgyleTable
                        columns={COLUMNS}
                        data={filteredPts}
                        noMaxHeight
                        edRefresh
                        stickyHeader
                        minHeightRowCell={43}
                    />
                }

                <div ref={refContainer}/>
            </div>
        </>
    );
};

export default StatisticED;